import { ArrowRightSubmit, Send } from '@ori-ui/icons';
import { styled, Typography } from '@ori-ui/mui';
import Cookies from 'js-cookie';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import {
  newsletterPopupCookieKey,
  newsletterPopupCookieValue,
  newsletterSubscribeCookieKey,
  newsletterSubscribeCookieValue,
} from '../../constants';
import { useFooterContext } from '../../hooks';
import { MobileOfficeRoot } from '../MobileOffice';
import { NewsletterDialog } from './NewsletterDialog';

const PREFIX = 'Subscribe';

const classes = {
  icon: `${PREFIX}-icon`,
  side: `${PREFIX}-side`,
  inner: `${PREFIX}-inner`,
  newsletterIcon: `${PREFIX}-newsletterIcon`,
  arrowIcon: `${PREFIX}-arrowIcon`,
  title: `${PREFIX}-title`,
};

const Root = styled(MobileOfficeRoot)(({ theme }) => ({
  backgroundColor: '#007681',
  color: theme.palette.common.white,
  columnGap: theme.spacing(2),
  cursor: 'pointer',
  display: 'grid',
  gridTemplateColumns: '1fr 10fr 1fr',
  minHeight: '54px',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    gridTemplateColumns: '1fr',
    minHeight: '50px',
  },

  [`& .${classes.icon}`]: {
    marginRight: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(1),
    },
  },

  [`& .${classes.side}`]: {
    alignItems: 'center',
    display: 'flex',
    fontSize: '36px',
    justifySelf: 'end',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },

  [`& .${classes.inner}`]: {
    alignItems: 'center',
    display: 'flex',
    flex: 2,
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 3),
    },
  },

  [`& .${classes.newsletterIcon}`]: {
    alignItems: 'center',
    display: 'flex',
    fontSize: '32px',
    marginRight: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      fontSize: '27px',
      marginRight: theme.spacing(2),
    },
  },

  [`& .${classes.arrowIcon}`]: {
    marginRight: theme.spacing(6),
  },

  [`& .${classes.title}`]: {
    color: theme.palette.common.white,
    fontSize: '17px',
    lineHeight: '22px',
    margin: theme.spacing(3, 0),
    wordBreak: 'break-all',

    [theme.breakpoints.down('md')]: {
      fontSize: '14px',
      lineHeight: '18px',
    },
  },
}));

export const Subscribe: FC = () => {
  const { data, isMobileOffice } = useFooterContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [src, setSrc] = useState<'OnlineFooter' | 'OnlinePopup'>('OnlinePopup');

  const { footer, popup } = data?.newsletterSubsciptionConfiguration ?? { footer: undefined, popup: undefined };
  const cookieExpireDays = 1;

  // handling only automatic showing of popup
  useEffect(() => {
    const milliseconds = 1000;

    // check if newsletter and popup is enabled
    if (!footer?.enabled || !popup?.enabled) {
      return () => {};
    }

    const popupDelay = popup.delayInSeconds;
    // check if time is set
    if (!popupDelay || popupDelay < 1) {
      return () => {};
    }

    // checking if person is already subscribed
    const subscribedCookieValue = Cookies.get(newsletterSubscribeCookieKey);
    if (subscribedCookieValue && subscribedCookieValue === newsletterSubscribeCookieValue) {
      return () => {};
    }

    // checking if the popup was opened before
    const popupCookieValue = Cookies.get(newsletterPopupCookieKey);
    if (!openDialog && popupCookieValue && popupCookieValue === newsletterPopupCookieValue) {
      return () => {};
    }

    // setting up delayed popup and setting cookie that will expire in number of days set in Sitecore
    const timer = setTimeout(() => {
      Cookies.set(newsletterPopupCookieKey, newsletterPopupCookieValue, {
        expires: popup.displayFrequencyInDays,
      });
      setOpenDialog(true);
    }, popupDelay * milliseconds);

    // if dialog is open we do not need delayed opening
    if (openDialog) {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [openDialog, footer, popup]);

  // return null if newsletter is not enabled
  if (!footer?.enabled) {
    return null;
  }

  // setting cookie and opening dialog
  function handleClick() {
    return () => {
      Cookies.set(newsletterPopupCookieKey, newsletterPopupCookieValue, {
        expires: popup?.displayFrequencyInDays ?? cookieExpireDays,
      });
      setSrc('OnlineFooter');
      setOpenDialog(true);
    };
  }

  function handleCloseDialog() {
    setOpenDialog(false);
  }

  return (
    <>
      <Root
        role="button"
        tabIndex={0}
        isMobileOfficeFull={isMobileOffice}
        onClick={handleClick()}
      >
        <div className={classes.side} />
        <div className={classes.inner}>
          <div className={classes.newsletterIcon}>
            <Send fontSize="inherit" />
          </div>
          <Typography
            variant="subtitle2"
            align="center"
            className={classes.title}
          >
            {footer.title}
          </Typography>
        </div>
        <div className={classes.side}>
          <ArrowRightSubmit
            fontSize="inherit"
            className={classes.arrowIcon}
          />
        </div>
      </Root>
      <NewsletterDialog
        open={openDialog}
        src={src}
        handleCloseDialog={handleCloseDialog}
      />
    </>
  );
};
